import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import { logisticsApi } from 'api/logistics';
import { CrossDockPurchaseOrderAdditionalFeesCard } from 'components/logistics/components/crossDockPurchaseOrders/create/CrossDockPurchaseOrderAdditionalFeesCard';
import { CrossDockPurchaseOrderCreatePageHeader } from 'components/logistics/components/crossDockPurchaseOrders/create/CrossDockPurchaseOrderCreatePageHeader';
import { CrossDockPurchaseOrderCreatePrimaryCard } from 'components/logistics/components/crossDockPurchaseOrders/create/CrossDockPurchaseOrderCreatePrimaryCard';
import { useCrossDockPurchaseOrderAdditionalFeeTypes } from 'components/logistics/components/crossDockPurchaseOrders/useCrossDockPurchaseOrderAdditionalFeeTypes';
import { crossDockPurchaseOrderQueryKeys } from 'components/logistics/components/crossDockPurchaseOrders/useCrossDockPurchaseOrdersSearch';
import {
    DEFAULT_CROSS_DOCK_PURCHASE_ORDER_DRAFT,
    validateCrossDockPurchaseOrderCreate,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderCreateValidation';
import { CROSS_DOCK_PURCHASE_ORDERS_LIST_PATH } from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderUtils';
import { UnderConstruction } from 'shared';
import { useIsFeatureOn } from 'shared/hooks/featureFlags/useIsFeatureOn';

const CROSS_DOCK_FEES_FEATURE_FLAG = 'CROSS_DOCK_FEES';
export function CreateCrossDockPurchaseOrder() {
    const history = useHistory();
    const queryClient = useQueryClient();
    const { data: crossDockPurchaseOrdersEnabled, isLoading: isFeatureLoading } =
        useIsFeatureOn(CROSS_DOCK_FEES_FEATURE_FLAG);
    const { data: additionalFeeTypes = [], isLoading: isAdditionalFeeTypesLoading } =
        useCrossDockPurchaseOrderAdditionalFeeTypes({
            enabled: Boolean(crossDockPurchaseOrdersEnabled),
        });

    const [draft, setDraft] = useState(DEFAULT_CROSS_DOCK_PURCHASE_ORDER_DRAFT);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    const validation = useMemo(
        () => validateCrossDockPurchaseOrderCreate(draft, { additionalFeeTypes }),
        [additionalFeeTypes, draft],
    );

    const handleBack = useCallback(() => {
        history.push(CROSS_DOCK_PURCHASE_ORDERS_LIST_PATH);
    }, [history]);

    const handleSave = useCallback(async () => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        setHasAttemptedSubmit(true);
        if (!validation.isValid || !validation.submitBody) {
            isSubmittingRef.current = false;
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await logisticsApi.createCrossDockPurchaseOrder(validation.submitBody);
            if (!response?.status) {
                toast.error(response?.msg ?? 'Error creating cross dock purchase order');
                return;
            }

            await queryClient.invalidateQueries({ queryKey: crossDockPurchaseOrderQueryKeys.all });
            toast.success('Cross dock purchase order created');
            history.push(CROSS_DOCK_PURCHASE_ORDERS_LIST_PATH);
        } catch (error) {
            toast.error(error?.message ?? 'Error creating cross dock purchase order');
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    }, [history, queryClient, validation]);

    if (isFeatureLoading) {
        return null;
    }

    if (!crossDockPurchaseOrdersEnabled) {
        return <UnderConstruction title="Cross Dock Purchase Orders" />;
    }

    return (
        <div className="flex min-h-screen flex-col gap-6 px-4 py-4">
            <CrossDockPurchaseOrderCreatePageHeader
                title="Create Cross Dock Purchase Order"
                statusLabel="Creating"
                onBack={handleBack}
                onCancel={handleBack}
                onSave={handleSave}
                isDisabled={isSubmitting || isAdditionalFeeTypesLoading}
            />

            <CrossDockPurchaseOrderCreatePrimaryCard
                draft={draft}
                onDraftChange={setDraft}
                showValidation={hasAttemptedSubmit}
                primaryErrors={validation.primaryErrors}
                primaryFieldErrors={validation.primaryFieldErrors}
                additionalFeeTypes={additionalFeeTypes}
                feeFieldErrors={validation.feeFieldErrors}
            />

            <CrossDockPurchaseOrderAdditionalFeesCard
                draft={draft}
                onDraftChange={setDraft}
                additionalFeeTypes={additionalFeeTypes}
                isLoadingFeeTypes={isAdditionalFeeTypesLoading}
                showValidation={hasAttemptedSubmit}
                feeErrors={validation.feeErrors}
                feeFieldErrors={validation.feeFieldErrors}
            />
        </div>
    );
}

CreateCrossDockPurchaseOrder.displayName = 'CreateCrossDockPurchaseOrder';
